import { Recipe } from '@/types/recipe';
import { useRecipeStore } from '@/stores/recipeStore';
import { searchRecipesForAssistant, getRecipeCount } from '@/lib/supabase-service';

interface RecipeContext {
  currentRecipe: Recipe | null;
  allRecipes: Recipe[];
  filteredRecipes: Recipe[];
}

class RecipeContextManager {
  private static instance: RecipeContextManager;
  private currentRecipe: Recipe | null = null;
  private listeners: Set<(context: RecipeContext) => void> = new Set();

  static getInstance(): RecipeContextManager {
    if (!RecipeContextManager.instance) {
      RecipeContextManager.instance = new RecipeContextManager();
    }
    return RecipeContextManager.instance;
  }

  setCurrentRecipe(recipe: Recipe | null): void {
    // Only update if the recipe has actually changed
    if (this.currentRecipe?.id === recipe?.id &&
        this.currentRecipe?.title === recipe?.title) {
      return; // No change, skip update
    }
    
    this.currentRecipe = recipe;
    this.notifyListeners();
  }

  getCurrentRecipe(): Recipe | null {
    return this.currentRecipe;
  }

  getContext(): RecipeContext {
    // Get recipes from the store
    const store = useRecipeStore.getState();
    return {
      currentRecipe: this.currentRecipe,
      allRecipes: store.recipes,
      filteredRecipes: store.getFilteredRecipes()
    };
  }

  subscribe(listener: (context: RecipeContext) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    const context = this.getContext();
    this.listeners.forEach(listener => listener(context));
  }

  // Helper methods for the assistant
  findRecipeByTitle(title: string): Recipe | null {
    const store = useRecipeStore.getState();
    const normalizedTitle = title.toLowerCase().trim();
    
    return store.recipes.find(recipe => 
      recipe.title.toLowerCase().includes(normalizedTitle)
    ) || null;
  }

  findRecipesByIngredient(ingredient: string): Recipe[] {
    const store = useRecipeStore.getState();
    const normalizedIngredient = ingredient.toLowerCase().trim();
    
    return store.recipes.filter(recipe => 
      recipe.ingredients.toLowerCase().includes(normalizedIngredient)
    );
  }

  findRecipesByCategory(category: string): Recipe[] {
    const store = useRecipeStore.getState();
    const normalizedCategory = category.toLowerCase().trim();
    
    return store.recipes.filter(recipe => 
      recipe.category?.toLowerCase() === normalizedCategory
    );
  }

  findRecipesByTag(tag: string): Recipe[] {
    const store = useRecipeStore.getState();
    const normalizedTag = tag.toLowerCase().trim();
    
    return store.recipes.filter(recipe => 
      recipe.tags.some(t => t.toLowerCase().includes(normalizedTag))
    );
  }

  getFavoriteRecipes(): Recipe[] {
    const store = useRecipeStore.getState();
    return store.recipes.filter(recipe => recipe.isFavorite);
  }

  getSharedRecipes(): Recipe[] {
    const store = useRecipeStore.getState();
    return store.recipes.filter(recipe => recipe.share);
  }

  getRecipeStats(): { total: number; favorites: number; categories: Record<string, number> } {
    const store = useRecipeStore.getState();
    const recipes = store.recipes;
    
    const categories: Record<string, number> = {};
    recipes.forEach(recipe => {
      if (recipe.category) {
        categories[recipe.category] = (categories[recipe.category] || 0) + 1;
      }
    });

    return {
      total: recipes.length,
      favorites: recipes.filter(r => r.isFavorite).length,
      categories
    };
  }

  // Assistant-specific methods for scalable database search
  async searchRecipesForAssistant(searchTerm: string, limit: number = 10): Promise<Recipe[]> {
    try {
      const results = await searchRecipesForAssistant(searchTerm, limit);
      return results;
    } catch {
      return [];
    }
  }

  async getTotalRecipeCount(): Promise<number> {
    try {
      return await getRecipeCount();
    } catch {
      // Fallback to store count
      const store = useRecipeStore.getState();
      return store.recipes.length;
    }
  }

  // Enhanced recipe stats that shows true collection size
  async getEnhancedRecipeStats(): Promise<{ total: number; favorites: number; categories: Record<string, number> }> {
    try {
      const totalCount = await this.getTotalRecipeCount();
      const store = useRecipeStore.getState();
      
      // For categories and favorites, we use the store data (which represents current view)
      // But we show the true total count from database
      const recipes = store.recipes;
      const categories: Record<string, number> = {};
      recipes.forEach(recipe => {
        if (recipe.category) {
          categories[recipe.category] = (categories[recipe.category] || 0) + 1;
        }
      });

      return {
        total: totalCount, // True database count
        favorites: recipes.filter(r => r.isFavorite).length,
        categories
      };
    } catch {
      // Fallback to regular stats
      return this.getRecipeStats();
    }
  }
}

export const recipeContextManager = RecipeContextManager.getInstance();

// Export convenience functions
export const setCurrentRecipe = (recipe: Recipe | null) => recipeContextManager.setCurrentRecipe(recipe);
export const getCurrentRecipe = () => recipeContextManager.getCurrentRecipe();
export const getRecipeContext = () => recipeContextManager.getContext();
export const subscribeToRecipeContext = (listener: (context: RecipeContext) => void) => 
  recipeContextManager.subscribe(listener);

export default recipeContextManager;